import { FontDescription } from "./sdfText";

import * as pastaDescription from "./armonela72.json";
import pasta64 from "data-url:./armonela256.png";
import * as novareseDescription from "./novarese.json";
import novarese from "data-url:./novarese.png";

import { xoshiro128ss } from "./random";

export interface LoadedFont {
  id: number;
  description: FontDescription;
  texture: WebGLTexture;
  scale: number;
}

export type FontName = "arial" | "pasta" | "novarese";
export type Fonts = Record<FontName, LoadedFont>;

export interface LoadedState {
  fonts: Fonts;
  textures: {
    metal: WebGLTexture;
    metalNormal: WebGLTexture;
    noise: WebGLTexture;
    splashBg: WebGLTexture;
    splashFg: WebGLTexture;
    splashMask: WebGLTexture;
    sparkle: WebGLTexture;
    sky: WebGLTexture;
  };
}

interface TextureLoadingSettings {
  noRepeat?: boolean;
}

async function loadTexture(
  gl: WebGL2RenderingContext,
  url: string,
  settings?: TextureLoadingSettings,
): Promise<WebGLTexture> {
  const image = new Image();
  image.src = url;
  await image.decode();

  const texture = gl.createTexture();

  if (!texture) {
    throw new Error("Failed to create texture");
  }

  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);

  if (settings?.noRepeat) {
    // Clamp
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }

  // Flip Y
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, 1);

  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
  gl.generateMipmap(gl.TEXTURE_2D);

  return texture;
}

async function loadFont({
  gl,
  description,
  textureUrl,
  id,
  scale,
}: {
  gl: WebGL2RenderingContext;
  description: FontDescription;
  textureUrl: string;
  id: number;
  scale: number;
}): Promise<LoadedFont> {
  // Load image
  const image = new Image();
  image.src = textureUrl;
  await image.decode();

  const texture = gl.createTexture();

  if (!texture) {
    throw new Error("Failed to create texture");
  }

  gl.bindTexture(gl.TEXTURE_2D, texture);
  // Clamp
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  // Disable interpolation
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
  gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
  //gl.generateMipmap(gl.TEXTURE_2D);

  return {
    description,
    texture,
    id,
    scale,
  };
}

export function generateRandomNoiseTexture(
  glContext: WebGL2RenderingContext,
): WebGLTexture {
  const size = 256;
  const data = new Uint8Array(size * size * 4);

  const rng = xoshiro128ss(3447226902, 3732301963, 1546073757, 602736451);

  for (let i = 0; i < size * size * 4; i++) {
    data[i] = Math.floor(rng() * 255);
  }

  const texture = glContext.createTexture();

  if (!texture) {
    throw new Error("Failed to create texture");
  }

  glContext.bindTexture(glContext.TEXTURE_2D, texture);
  // Repeat
  glContext.texParameteri(
    glContext.TEXTURE_2D,
    glContext.TEXTURE_WRAP_S,
    glContext.REPEAT,
  );
  glContext.texParameteri(
    glContext.TEXTURE_2D,
    glContext.TEXTURE_WRAP_T,
    glContext.REPEAT,
  );
  // Enable interpolation
  glContext.texParameteri(
    glContext.TEXTURE_2D,
    glContext.TEXTURE_MAG_FILTER,
    glContext.LINEAR,
  );
  glContext.texParameteri(
    glContext.TEXTURE_2D,
    glContext.TEXTURE_MIN_FILTER,
    glContext.LINEAR_MIPMAP_LINEAR,
  );

  glContext.pixelStorei(glContext.UNPACK_ALIGNMENT, 1);
  glContext.pixelStorei(
    glContext.UNPACK_COLORSPACE_CONVERSION_WEBGL,
    glContext.NONE,
  );
  glContext.texImage2D(
    glContext.TEXTURE_2D,
    0,
    glContext.RGBA,
    size,
    size,
    0,
    glContext.RGBA,
    glContext.UNSIGNED_BYTE,
    data,
  );

  glContext.generateMipmap(glContext.TEXTURE_2D);

  return texture;
}

type DeepPartial<T> = {
  [P in keyof T]?: DeepPartial<T[P]>;
};

type AssetLoader = (
  state: DeepPartial<LoadedState>,
  glContext: WebGL2RenderingContext,
) => Promise<void>;

function fontLoader({
  id,
  name: fontName,
  description,
  textureUrl,
  scale,
}: {
  id: number;
  name: FontName;
  description: FontDescription;
  textureUrl: string;
  scale: number;
}): AssetLoader {
  return async (state, gl) => {
    const font = await loadFont({ gl, description, textureUrl, id, scale });
    state.fonts = state.fonts || {};
    state.fonts[fontName] = font;
  };
}

function textureLoader(
  textureName: keyof LoadedState["textures"],
  url: URL,
  settings?: TextureLoadingSettings,
): AssetLoader {
  return async (state, glContext) => {
    const texture = await loadTexture(glContext, url.href, settings);
    state.textures = state.textures || {};
    state.textures[textureName] = texture;
  };
}

async function noiseLoader(
  state: DeepPartial<LoadedState>,
  glContext: WebGL2RenderingContext,
): Promise<void> {
  const noise = generateRandomNoiseTexture(glContext);
  state.textures = state.textures || {};
  state.textures.noise = noise;
}

const assets = [
  fontLoader({
    id: 0,
    name: "pasta",
    description: pastaDescription,
    textureUrl: pasta64,
    scale: 0.001,
  }),
  fontLoader({
    id: 1,
    name: "novarese",
    description: novareseDescription,
    textureUrl: novarese,
    scale: 0.002,
  }),
  textureLoader("metal", new URL("textures/steel.png", import.meta.url)),
  textureLoader(
    "metalNormal",
    new URL("textures/steel_n.png", import.meta.url),
  ),
  textureLoader(
    "splashBg",
    new URL("textures/makaronin_ystavaet_bg.png", import.meta.url),
  ),
  textureLoader(
    "splashFg",
    new URL("textures/makaronin_ystavaet_fg.png", import.meta.url),
  ),
  textureLoader(
    "splashMask",
    new URL("textures/makaronin_ystavaet_mask.png", import.meta.url),
  ),
  textureLoader("sparkle", new URL("textures/sparkle.png", import.meta.url)),
  textureLoader("sky", new URL("textures/sky.png", import.meta.url), {
    noRepeat: true,
  }),
  noiseLoader,
];

export const TOTAL_ASSETS = assets.length;

export async function loadAllAssets(
  glContext: WebGL2RenderingContext,
  onProgress: (progress: number, message: string) => void,
): Promise<LoadedState> {
  let loadTotal = 0;

  const advanceProgress = <T>(x: T) => {
    loadTotal++;
    onProgress(
      loadTotal / TOTAL_ASSETS,
      `Loaded asset ${loadTotal}/${TOTAL_ASSETS}`,
    );
    return x;
  };

  let state: DeepPartial<LoadedState> = {};
  const promises = assets.map((loader) =>
    loader(state, glContext).then(advanceProgress),
  );

  await Promise.all(promises);

  return state as LoadedState;
}
